import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import SelectedDevice from './SelectedDevice';

import { useGetUnmountedHubs } from 'hooks/InteractiveMap/useGetUnmountedHubs';

export interface AddDeviceOnMapModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  plan_slug: string;
  obj_slug: string;
  onSetTempMarker: (device: any) => void;
  tempMarkers?: { position: any; device: any }[];
}

const AddHubModal: React.FC<AddDeviceOnMapModalProps> = ({
  isOpen,
  obj_slug,
  setIsOpen,
  onSetTempMarker,
  tempMarkers = [],
}) => {
  const { data } = useGetUnmountedHubs(obj_slug);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const { t } = useTranslation();

  // Filter out hubs that are already added as temporary markers
  const availableHubs = data?.filter(hub => !tempMarkers.some(marker => marker.device.slug === hub.slug)) || [];

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, setIsOpen]);

  const handleDeviceSelect = (deviceSlug: string) => {
    setSelectedDevice(deviceSlug);
  };

  const onReset = () => {
    setIsOpen(false);
  };

  const setOnMapTempMarker = () => {
    if (selectedDevice) {
      const device = availableHubs.find(d => d.slug === selectedDevice);
      if (device) {
        onSetTempMarker({ ...device, isHub: true });
        setIsOpen(false);
        setSelectedDevice(null); // Reset selection after adding
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '20px',
        }}
        onClick={onReset}
      >
        {/* Modal Content */}
        <div
          style={{
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px 16px 24px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h5 style={{ margin: 0, fontWeight: 600, color: '#333' }}>{t('MapModals.AddHub.title')}</h5>
            <button
              onClick={onReset}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
            {availableHubs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                {t('MapModals.AddHub.noAvailableHubs')}
              </div>
            ) : (
              <div className="row">
                {availableHubs.map((trap, index) => (
                  <SelectedDevice
                    key={index}
                    device={trap}
                    isSelected={selectedDevice === trap.slug}
                    onSelect={handleDeviceSelect}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px 24px 24px',
              borderTop: '1px solid #eee',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}
          >
            <button
              onClick={onReset}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: '#fff',
                color: '#666',
                cursor: 'pointer',
                fontSize: '14px',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = '#fff';
              }}
            >
              {t('MapModals.AddHub.cancel')}
            </button>
            <button
              onClick={setOnMapTempMarker}
              disabled={!selectedDevice}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                background: selectedDevice ? '#6f42c1' : '#ccc',
                color: '#fff',
                cursor: selectedDevice ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: 500,
              }}
              onMouseEnter={e => {
                if (selectedDevice) {
                  e.currentTarget.style.backgroundColor = '#5a32a3';
                }
              }}
              onMouseLeave={e => {
                if (selectedDevice) {
                  e.currentTarget.style.backgroundColor = '#6f42c1';
                }
              }}
            >
              {t('MapModals.AddHub.setOnMap')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddHubModal;
